import {
  AgFinancing,
  BackgroundCheck,
  BackgroundCheckDisplayStatus,
  ContactRiskClassification,
  OpportunityContactRole,
  RiskClassificationLevel,
  RiskClassificationResult,
  SerasaReport,
  Phase1,
  AgFinancingPhaseStatus,
  Phase2,
  AgFinancingPhase2Status,
  Phase3,
  AgFinancingPhase3Status,
  Phase4,
  AgFinancingPhase4Status,
  BacenReport,
  OpportunityType,
  OpportunityRegional
} from '@indigo-ag/schema';
import { ListSubItem } from './context/Phase1.types';
import { Dictionary } from 'lodash';
import { getFormattedTaxIdentifier } from 'features/brazil-grower/pages/ContactDetailsPage/utils';
import { brazil, StepStatus } from 'shared';
import { getOverallSerasaBacenStatus, SerasaBacenStatus } from '../BrazilGrowerFinance/phases/Phase1.utils';
import i18next from 'core/i18n';

const STATUS_MAP: Record<SerasaBacenStatus, BackgroundCheckDisplayStatus> = {
  CLEARED: BackgroundCheckDisplayStatus.CLEARED,
  FAILED: BackgroundCheckDisplayStatus.FAILED,
  UNRESOLVED: BackgroundCheckDisplayStatus.UNRESOLVED
};

const ALTERNATIVE_FLOW_OPPORTUNITY_TYPES = [
  OpportunityType.CASH,
  OpportunityType.BONUS,
  OpportunityType.DEMAND_GENERATION,
  OpportunityType.SAMPLE
];

export const getSerasaBacenSubItems = (
  backgroundChecks: BackgroundChecks[],
  serasaReport?: SerasaReport,
  bacenReport?: BacenReport
) => {
  return backgroundChecks.map<ListSubItem>(({ taxIdentifier, text, key }) => {
    const serasa = serasaReport?.reports?.find(report => {
      return brazil.cleanCpfCnpjValue(report.taxIdentifier) === brazil.cleanCpfCnpjValue(taxIdentifier);
    });

    const bacen = bacenReport?.reports?.find(report => {
      return brazil.cleanCpfCnpjValue(report.taxIdentifier) === brazil.cleanCpfCnpjValue(taxIdentifier);
    });

    const status = STATUS_MAP[getOverallSerasaBacenStatus(serasa, bacen)];

    return {
      text,
      status,
      key,
      taxIdentifier
    };
  });
};

interface BackgroundChecks {
  text: string;
  key: string;
  taxIdentifier: string;
  displayStatus: BackgroundCheckDisplayStatus;
}

export const getBackgroundCheckSubItems = (backgroundChecks: BackgroundChecks[]) => {
  return backgroundChecks.map<ListSubItem>(({ taxIdentifier, displayStatus, text, key }) => ({
    text,
    status: displayStatus,
    key,
    taxIdentifier
  }));
};

const getRiskClassificationStatus = ({ riskClassificationLevel, isExpired }: ContactRiskClassification) => {
  if (!riskClassificationLevel || riskClassificationLevel === RiskClassificationLevel.NOT_CLASSIFIED) {
    return BackgroundCheckDisplayStatus.IN_PROGRESS;
  }

  if (
    (riskClassificationLevel &&
      [RiskClassificationLevel.VERY_HIGH, RiskClassificationLevel.HIGH].includes(riskClassificationLevel)) ||
    isExpired
  ) {
    return BackgroundCheckDisplayStatus.FAILED;
  }

  return BackgroundCheckDisplayStatus.CLEARED;
};

export const getFundsApprovalItems = (
  backgroundChecks: BackgroundChecks[],
  contactRiskClassification?: RiskClassificationResult
) => {
  const list = contactRiskClassification?.classifications ?? [];

  return backgroundChecks.map<ListSubItem>(({ text, key, taxIdentifier }) => {
    const classification = list.find(
      item => brazil.cleanCpfCnpjValue(item.contactTaxIdentifier) === brazil.cleanCpfCnpjValue(taxIdentifier)
    );
    const status = classification
      ? getRiskClassificationStatus(classification)
      : BackgroundCheckDisplayStatus.IN_PROGRESS;

    return {
      text,
      status,
      key,
      taxIdentifier
    };
  });
};

interface Params {
  opportunityContactRoles?: OpportunityContactRole[];
  backgroundCheckMap?: Dictionary<BackgroundCheck>;
  agFinancing: AgFinancing;
}

export const getBackgroundCheckItems = ({ opportunityContactRoles, backgroundCheckMap, agFinancing }: Params) => {
  const backgroundCheckItems =
    opportunityContactRoles && backgroundCheckMap
      ? opportunityContactRoles.map(contactRole => {
          const taxIdentifier = getFormattedTaxIdentifier(contactRole?.contact.taxIdentifier) ?? '';
          return {
            text: contactRole.contact.name,
            displayStatus: backgroundCheckMap[taxIdentifier]?.displayStatus,
            key: contactRole.contactId,
            taxIdentifier
          };
        })
      : [];

  const clientIsAContact = opportunityContactRoles?.some(
    item => item.contact.taxIdentifier === agFinancing.grower?.cpfOrCnpj
  );

  if (agFinancing.grower && backgroundCheckMap && !clientIsAContact) {
    const taxIdentifier = getFormattedTaxIdentifier(agFinancing.grower.cpfOrCnpj) ?? '';
    backgroundCheckItems.push({
      text: agFinancing.grower.name,
      displayStatus: backgroundCheckMap[taxIdentifier]?.displayStatus,
      key: agFinancing.grower.id,
      taxIdentifier
    });
  }

  return backgroundCheckItems;
};

const PHASE1_STATUS_MAP = {
  [AgFinancingPhaseStatus.CLEAR]: StepStatus.COMPLETED,
  [AgFinancingPhaseStatus.DENIED]: StepStatus.DENIED,
  [AgFinancingPhaseStatus.NOT_STARTED]: StepStatus.UNCOMPLETED,
  [AgFinancingPhaseStatus.IN_PROGRESS]: StepStatus.UNCOMPLETED,
  [AgFinancingPhaseStatus.AWAITING_MANAGER_APPROVAL]: StepStatus.UNCOMPLETED
};

export const getStatus1 = (phase?: Phase1): StepStatus => {
  return !phase ? StepStatus.COMPLETED : PHASE1_STATUS_MAP[phase.status ?? AgFinancingPhaseStatus.NOT_STARTED];
};

export const getStatus2 = (phase?: Phase2): StepStatus => {
  if (!phase) {
    return StepStatus.UNCOMPLETED;
  }

  if (phase.status === AgFinancingPhase2Status.APPROVED) {
    return StepStatus.COMPLETED;
  } else if (phase.status === AgFinancingPhase2Status.DENIED) {
    return StepStatus.DENIED;
  } else {
    return StepStatus.UNCOMPLETED;
  }
};

export const getStatus3 = (phase?: Phase3): StepStatus => {
  if (!phase) {
    return StepStatus.UNCOMPLETED;
  }

  if (phase.status === AgFinancingPhase3Status.APPROVED) {
    return StepStatus.COMPLETED;
  } else if (phase.status === AgFinancingPhase3Status.DENIED) {
    return StepStatus.DENIED;
  } else {
    return StepStatus.UNCOMPLETED;
  }
};

export const getStatus4 = (phase?: Phase4): StepStatus => {
  if (!phase) {
    return StepStatus.UNCOMPLETED;
  }

  if (phase.status === AgFinancingPhase4Status.SUBMITTED_MINUTA) {
    return StepStatus.COMPLETED;
  }

  return StepStatus.UNCOMPLETED;
};

export const isOpportunityAlternativeFlow = (agFinancing: AgFinancing) => {
  if (!agFinancing?.opportunityType) {
    return false;
  }

  return ALTERNATIVE_FLOW_OPPORTUNITY_TYPES.includes(agFinancing.opportunityType);
};

export const checkIfDisablePhase2 = (agFinancing: AgFinancing) => {
  return (
    (agFinancing.phase1?.status !== AgFinancingPhaseStatus.CLEAR || isOpportunityAlternativeFlow(agFinancing)) &&
    agFinancing.opportunityRegional !== OpportunityRegional.INDUSTRIAL_SALE_AND_ALLIANCE
  );
};

export const getPhase2Title = (agFinancing: AgFinancing) => {
  if (agFinancing.opportunityType === OpportunityType.BARTER_GUARANTEE) {
    return i18next.t('collateralAnalysis');
  }
  if (agFinancing.opportunityType === OpportunityType.BARTER_LIMIT) {
    return i18next.t('creditAnalysis');
  }
  return i18next.t('collateralAndCreditAnalysis');
};
